/**
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 *
 * THIS FILE IS MANAGED CENTRALLY BY THE `common-code` REPO.
 * IT COULD BE AUTO-REPLACED AT ANY TIME.
 * DO NOT MAKE CUSTOM CHANGES TO THIS FILE.
 * @see https://gitlab.com/dea-aero/development/common-code
 *
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 */
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Link} from "@mui/material";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import ClearIcon from '@mui/icons-material/Clear';
import Container from "@mui/material/Container";

import {Warning, Success, Info, Error} from "../part/Msg";
import {useApiCallback, useApiStore} from "../util/Api";
import PersonAvatar from "../part/PersonAvatar";
import {AuthorityChip} from "./Preferences";
import {useLoginStore} from "../useLoginStore";
import DataGrid from "../part/DataGrid";


const GroupInfoLink = ({group, navigate}) => (
    <Link color="inherit"
          sx={{lineHeight: '160%', cursor: 'pointer'}}
          onClick={() => navigate(`/group/${group}`)}
    >{group}</Link>
)

const Code = ({children, ...other}) => (
    <Typography variant="code" px={3} py={1} {...other} sx={{backgroundColor:'#8888', borderRadius:2}}>{children}</Typography>
)

export default function PersonInfo() {
    const {email} = useParams()
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const setAsUser = useApiStore(state => state.setAsUser);
    const canUser = useLoginStore(state => state.canUser);
    const [allowedIps, setAllowedIps] = useState([])

    const userIsAdmin = canUser('POST', '/api/acl')
    const canUserSeeGroups = canUser('GET', `/api/person/groups/${email}`)

    const getAllowedIps = useApiCallback('GET', `/api/user/allowedips/${email}`, {setLoading, onDone: setAllowedIps})
    useEffect(getAllowedIps, [getAllowedIps]);

    const [person, setPerson] = useState({})
    const getPerson = useApiCallback('GET', `/api/person/${email}`, {setLoading, onDone: setPerson})
    useEffect(getPerson, [getPerson])

    const isInternalUser = !person?.userid?.startsWith('google-');

    const canResetPassword = useMemo(() => (
        isInternalUser && canUser('POST', `/api/resetpassword/${email}`)
    ), [canUser, email, isInternalUser])

    const [groups, setGroups] = useState({});
    const getGroups = useApiCallback('GET', `/api/person/groups/${email}`, {setLoading, onDone: setGroups})
    useEffect(() => {
        setGroups({})
        if (canUserSeeGroups) getGroups()
    }, [canUserSeeGroups, getGroups]);

    const sorted = useMemo(() => {
        const _ord = ['owner', 'manager', 'member']
        const arr = Object.keys(groups)
        arr.sort(
            (a, b) => groups[a] === groups[b] ? a - b : _ord.indexOf(groups[a]) - _ord.indexOf(groups[b])
        )
        return arr
    }, [groups])

    const asUserBtn = useMemo(() => (
        userIsAdmin
            ? <Button color="error" onClick={() => setAsUser(email)}>Become User</Button>
            : ''
    ), [email, setAsUser, userIsAdmin])

    const [newIp, setNewIp] = useState('')
    const [resetDone, setResetDone] = useState({})
    const resetPassword = useApiCallback('POST', `/api/user/passwordreset/${email}`, {setLoading, onDone: setResetDone})
    const disableUser = useApiCallback('POST', `/api/user/disable/${email}`, {setLoading, onDone: getPerson})
    const enableUser = useApiCallback('POST', `/api/user/enable/${email}`, {setLoading, onDone: getPerson})
    const removeAllowedIp = useApiCallback('DELETE', `/api/user/allowedip/${email}`, {
        setLoading,
        onDone: getAllowedIps
    })
    const addAllowedIp = useApiCallback('PUT', `/api/user/allowedip/${email}`, {setLoading, onDone: getAllowedIps})

    const resetPasswordBtn = useMemo(() => (
        canResetPassword
            ? <Button color="warning" onClick={() => resetPassword(email)}>Reset Password</Button>
            : ''
    ), [canResetPassword, email, resetPassword]);

    const reload = useCallback(() => navigate(`/person/${person.email}`), [navigate, person]);
    const savePerson = useApiCallback('POST', `/api/person/${email}`, {setLoading, onDone: reload})

    const [apikeys, setApikeys] = useState([])
    const fetchApikeys = useApiCallback('GET', `/api/apikeys/${email}`, {setLoading, onDone: setApikeys})
    useEffect(fetchApikeys, [fetchApikeys])

    const [newApiKey, setNewApiKey] = useState('');

    const onCreatedApiKey = useCallback(data => {
        // when new API key is created, display it.
        setNewApiKey(data);
        return fetchApikeys()
    }, [setNewApiKey, fetchApikeys])

    const createApiKey = useApiCallback('PUT', `/api/apikeys/${email}`, {setLoading, onDone: onCreatedApiKey})

    const revokeApiKey = useApiCallback('DELETE', `/api/apikeys/${email}`, {setLoading, onDone: () => fetchApikeys()})

    if (!person || !Object.keys(person).length) {
        return <Container>Person not found.</Container>
    }

    return <Container><Grid container>
        <Grid xs={12} md={6} item>
            <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                <PersonAvatar person={email} sx={{height: 100, width: 100}}/>
                <Typography variant="h5">Person <em>{email}</em></Typography>
            </Stack>

            <Stack direction="column" my={4} alignItems="left" spacing={1}>
                <Typography variant="h5" mt={3}>Member of</Typography>
                {loading ? 'loading…' : ''}
                {sorted.length ? '' : <Warning>Not a member of any groups</Warning>}
                {sorted.map(group => (
                    <Stack direction="row">
                        <AuthorityChip authority={groups[group]}/>
                        <GroupInfoLink group={group} navigate={navigate}/>
                    </Stack>
                ))}
            </Stack>
        </Grid>
        <Grid xs={12} md={6} item>

            {isInternalUser && canUser('POST', `/api/person/${email}`)
                ? <Stack direction="row" my={4} alignItems="center" spacing={2}>
                    <TextField
                        label="Name"
                        disabled={loading}
                        InputLabelProps={{shrink: true}}
                        value={person.name}
                        inputProps={{autoComplete: "none"}}
                        onChange={event => {
                            setPerson({...person, name: event.target.value})
                        }}
                        sx={{width: '300px', margin: 2}}/>
                    <TextField
                        label="Email"
                        disabled={loading}
                        InputLabelProps={{shrink: true}}
                        value={person.email}
                        inputProps={{autoComplete: "none"}}
                        onChange={event => {
                            setPerson({...person, email: event.target.value})
                        }}
                        sx={{width: '300px', margin: 2}}/>
                    <Button onClick={() => savePerson(person)}>Save</Button>
                </Stack>
                : ''
            }

            <Stack direction="column">
                <Typography variant="h5" mt={3} mb={2}>Security</Typography>
                {person?.has_basicauth
                    ? <Info>Password based login is enabled.</Info>
                    : <Info>Password based login is not enabled.</Info>
                }
                {person?.must_password_change_on_login
                    ? <Info>Password must be changed on next login.</Info>
                    : ''
                }
                {person?.has_basicauth
                    ? (
                        person?.mfa_devices?.length
                            ? <Success>User has multi-factor auth enabled.</Success>
                            : <Error>User does not have multi-factor auth enabled.</Error>
                    )
                    : ''
                }
                {allowedIps.length
                    ? <Success>User may login only from specific IPs.</Success>
                    : <Warning>No allowed IPs set - User can login from any IP.</Warning>
                }
            </Stack>

            <Stack direction="row" my={4} alignItems="left" spacing={2}>
                {isInternalUser
                    ? (
                        person.active
                            ? <Button color="warning" onClick={() => disableUser(email)}>Disable User</Button>
                            : <Button color="warning" onClick={() => enableUser(email)}>Enable User</Button>
                    )
                    : ''
                }
                {resetPasswordBtn}
                {asUserBtn}
            </Stack>

            <Typography variant="h5" mt={3} mb={2}>IP-Origin Login Restrictions</Typography>
            <Stack direction="row" mb={1} alignItems="left" spacing={2}>
                <TextField
                    label="IP Address"
                    disabled={loading}
                    InputLabelProps={{shrink: true}}
                    value={newIp}
                    inputProps={{autoComplete: 'none', maxLength: 18}}
                    onChange={event => setNewIp(event.target.value.replace('[^0-9.]', ''))}
                />
                <Button onClick={() => setNewIp('')} sx={{minWidth:2, p:2}}><ClearIcon/></Button>
                {allowedIps.some(i => i.ip === newIp)
                    ? <Button onClick={() => removeAllowedIp({ip: newIp})} color="warning">Remove Allowed IP</Button>
                    : <Button
                        color="success"
                        onClick={() => addAllowedIp({ip: newIp})}
                    >Add Allowed IP</Button>
                }
            </Stack>
            {allowedIps.length
                ? allowedIps.map(({ip, geo}) => (
                    <Stack direction="row" key={ip} spacing={2} justifyContent="left" alignItems="center" sx={{cursor:'pointer'}} onClick={() => setNewIp(ip)}>
                        <Code>{ip}</Code>
                        <Typography py={2}>{geo}</Typography>
                    </Stack>
                ))
                : ''
            }

            {resetDone?.newpassword
                ? <>
                    <Typography variant="h5" mt={3}>New password:</Typography>
                    <pre>{resetDone.newpassword}</pre>
                </>
                : ''
            }
        </Grid>

        {isInternalUser && canUser('PUT', `/api/apikeys/${email}`)
            ? <Stack direction="column" alignItems="left" spacing={2} sx={{
                justifyContent: "flex-start",
                alignItems: "flex-start",
            }}>
                <Typography variant="h5" mt={4} mb={1}>API Keys</Typography>
                <DataGrid
                    wrapperStyle={{height: 'auto', minHeight: '200px', maxWidth: '100%', width: '100%'}}
                    sx={{width:'100%', minWidth: '100%'}}
                    showToolbar={false}
                    rows={apikeys}
                    getRowId={r => r?.apikey}
                    columns={[
                        {field: 'apikey'},
                        {field: 'lastseen', width: 230},
                        {field: 'created', width: 230},
                        {field: 'expires', width: 230},
                        {
                            field: 'revoke',
                            renderCell: (r) => <Button size="small"
                                                       onClick={() => revokeApiKey({apikey: r.row.apikey})}>Revoke</Button>
                        }
                    ]}
                />
                <br/>
                <Button onClick={() => createApiKey()}>Generate API Key</Button>
                {newApiKey
                ? <Stack direction="row" alignItems="center" spacing={3}>
                    <Typography variant="h6" mt={3}>New API Key:</Typography>
                    <TextField value={newApiKey} readOnly="true" style={{border:'1px solid red', borderRadius:5, width:340}}/>
                    <Button onClick={() => setNewApiKey('')}>Hide</Button>
                </Stack>
                : ''
            }
            </Stack>
            : ''
        }
    </Grid></Container>
}
